import { useState, useMemo } from "react";
import type { WorldDocument, CardCategoryDocument, CardTemplateDocument } from "@private-fiction/story-core";
import { projectCategoryNavigation, worldFileGroups } from "@private-fiction/story-core";
import { AccountMediaImage } from "./AccountMedia";
import { FileTypePickerDialog } from "./FileTypePickerDialog";
interface VfsExplorerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  documents: readonly WorldDocument[];
  categories: readonly CardCategoryDocument[];
  templates: readonly CardTemplateDocument[];
  menuVisibility: Record<string, boolean>;
  onMenuVisibilityChange: (visibility: Record<string, boolean>) => void;
  onSelectDocument?: (document: WorldDocument) => void;
}

export function VfsExplorerDrawer({
  isOpen,
  onClose,
  documents,
  categories,
  templates,
  menuVisibility,
  onMenuVisibilityChange,
  onSelectDocument,
}: VfsExplorerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFolder, setActiveFolder] = useState<string>("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [inspectedDoc, setInspectedDoc] = useState<WorldDocument | null>(null);

  const groups = useMemo(() => worldFileGroups(documents, categories, templates), [documents, categories, templates]);
  const navigation = projectCategoryNavigation(groups.map(group => ({ ...group.category, ...((menuVisibility[group.category.id] ?? group.category.navigationVisible) !== undefined ? { navigationVisible: menuVisibility[group.category.id] ?? group.category.navigationVisible! } : group.documents.length > 0 ? { navigationVisible: true } : {}) })), "player");
  const selectedFolder = navigation.primary.some(category => category.id === activeFolder) ? activeFolder : navigation.primary[0]?.id;
  const filteredDocs = useMemo(() => {
    const list = groups.flatMap(group => group.documents);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      return list.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.kind.toLowerCase().includes(q) ||
          (d.summary && d.summary.toLowerCase().includes(q))
      );
    }
    return groups.find(group => group.category.id === selectedFolder)?.documents ?? [];
  }, [groups, selectedFolder, searchQuery]);

  if (!isOpen) return null;

  return (
    <aside className="vfs-explorer-overlay">
      <FileTypePickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)}
        choices={groups.map(group => ({ ...group.category, count: group.documents.length, selected: navigation.primary.some(category => category.id === group.category.id) }))}
        onToggle={(id, visible) => onMenuVisibilityChange({ ...menuVisibility, [id]: visible })} />
      <div className="vfs-explorer-backdrop" onClick={onClose} />
      <div className="vfs-explorer-drawer frosted-panel">
        {/* Header */}
        <div className="vfs-drawer__header">
          <div className="vfs-brand">
            <span className="vfs-icon">📁</span>
            <strong>คลังไฟล์ของโลก</strong>
            <small>PROJECT FILES</small>
          </div>
          <button type="button" className="drawer-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Search Bar */}
        <div className="vfs-search-bar">
          <input
            type="text"
            placeholder="ค้นหาเอกสารหรือไฟล์ทั้งหมดในโลกนี้..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={() => setSearchQuery("")}>
              ✕
            </button>
          )}
        </div>

        {/* Explorer Layout: Left Tree + Right File List / Inspector */}
        <div className="vfs-explorer-content">
          {/* Folders List (Left Column) */}
          <nav className="vfs-folder-tree">
            {navigation.primary.map((f) => {
              const count = groups.find(group => group.category.id === f.id)?.documents.length ?? 0;
              return (
                <button
                  key={f.id}
                  type="button"
                  className={`vfs-folder-item ${selectedFolder === f.id && !searchQuery ? "active" : ""}`}
                  onClick={() => {
                    setActiveFolder(f.id);
                    setSearchQuery("");
                    setInspectedDoc(null);
                  }}
                >
                  <span className="folder-icon">{f.icon}</span>
                  <span className="folder-label">{f.name}</span>
                  <span className="folder-count">{count}</span>
                </button>
              );
            })}
            <button type="button" className="file-types-more" onClick={() => setPickerOpen(true)}>＋ ประเภทไฟล์ ({navigation.additional.length} ที่ซ่อน)</button>
          </nav>

          {/* Files List / Preview (Right Column) */}
          <div className="vfs-files-container">
            {inspectedDoc ? (
              <div className="vfs-file-detail-view">
                <button
                  type="button"
                  className="back-to-list-btn"
                  onClick={() => setInspectedDoc(null)}
                >
                  ‹ กลับสู่รายการไฟล์
                </button>
                <div className="file-detail-card frosted-panel">
                  <header className="file-detail-header">
                    {typeof inspectedDoc.fields?.image === "string" && (
                      <div className="detail-banner">
                        <AccountMediaImage source={inspectedDoc.fields.image} alt={inspectedDoc.title} />
                      </div>
                    )}
                    <span className="detail-kind-pill">{inspectedDoc.kind}</span>
                    <h2>{inspectedDoc.title}</h2>
                    <small>ID: {inspectedDoc.id}</small>
                  </header>
                  <div className="detail-summary">
                    <p>{inspectedDoc.summary || "ไม่มีคำอธิบายสรุป"}</p>
                  </div>
                  {inspectedDoc.fields && Object.keys(inspectedDoc.fields).length > 0 && (
                    <div className="detail-fields-grid">
                      <strong>ข้อมูลในไฟล์:</strong>
                      {Object.entries(inspectedDoc.fields).map(([k, v]) => (
                        <div key={k} className="field-row">
                          <code>{k}:</code>
                          <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {onSelectDocument && (
                    <button
                      type="button"
                      className="inspect-action-btn"
                      onClick={() => {
                        onSelectDocument(inspectedDoc);
                        onClose();
                      }}
                    >
                      เปิดดูในแถบ Overview →
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="vfs-files-grid">
                {filteredDocs.length === 0 ? (
                  <div className="empty-files-hint">
                    <span>⌕</span>
                    <p>ไม่พบไฟล์ในโฟลเดอร์นี้</p>
                  </div>
                ) : (
                  filteredDocs.map((doc) => (
                    <div
                      key={doc.id}
                      className="vfs-file-card"
                      onClick={() => setInspectedDoc(doc)}
                    >
                      <div className="file-card-top">
                        <span className="file-kind-badge">{doc.kind}</span>
                      </div>
                      <strong className="file-title">{doc.title}</strong>
                      <p className="file-summary">{doc.summary || "เอกสารระบบ"}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
